import type { ReactNode } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Short warning shown in a highlighted banner. */
  warning?: ReactNode;
  /** Optional details/body rendered below the warning. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'success' | 'error' | 'warning';
  severity?: 'warning' | 'error' | 'info';
  loading?: boolean;
  /** Block the confirm action without showing a spinner (e.g. invalid input). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Generic confirm-before-act modal. Used to guard irreversible / money-moving
 * actions (placing an order, closing a position) with an explicit warning.
 */
export function ConfirmDialog({
  open, title, warning, children,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  confirmColor = 'primary', severity = 'warning', loading = false,
  confirmDisabled = false,
  onConfirm, onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <WarningAmberIcon sx={{ color: severity === 'error' ? '#ea3943' : '#f5a623' }} />
        {title}
      </DialogTitle>
      <DialogContent>
        {warning && (
          <Alert severity={severity} sx={{ mb: children ? 2 : 0 }}>{warning}</Alert>
        )}
        {typeof children === 'string'
          ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>{children}</Typography>
          : children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={loading} color="inherit">{cancelLabel}</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading || confirmDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Submitting…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
